import { createContext, useContext, useEffect, useMemo, useState } from 'react'

import {
    VerbitTheme,
    VerbitThemeProvider,
    extendTheme,
    theme as verbitThemeDefault,
    darkTheme as verbitThemeDark,
} from '@verbit-ai/verbit-ui-library'

import { themeCore, themePalettes } from 'src/themes'
import {
    THEME_TYPES,
    Theme,
    TEXT_BACKGROUND_COLORS_AMOUNT,
    DEFAULT_FONT_FAMILY_BY_LANGUAGE,
    DEFAULT_THEME_TYPE,
    TEXT_COLORS_AMOUNT,
    DEFAULT_TEXT_COLOR_INDEX,
    DEFAULT_TEXT_BACKGROUND_COLOR_INDEX,
    FontFamily,
    ConfigurableThemeFields,
    ThemeType,
    FONT_FAMILIES,
    DEFAULT_APP_BACKGROUND_COLOR_INDEX,
    APP_BACKGROUND_COLORS_AMOUNT,
    HEADER_HEIGHT_DEFAULT,
} from 'src/themes/types'
import {
    APP_BACKGROUND_COLOR_INDEX_QUERY_PARAM,
    FONT_FAMILY_QUERY_PARAM,
    TEXT_BACKGROUND_COLOR_INDEX_QUERY_PARAM,
    TEXT_COLOR_INDEX_QUERY_PARAM,
    THEME_TYPE_QUERY_PARAM,
    isChromakeyModeActive,
} from 'src/utils/env'
import { analyticsService } from 'src/services/AnalyticsService'
import { DEFAULT_LANGUAGE, Language, LANGUAGES_WITH_SPECIAL_FONTS, LanguageWithSpecialFont } from 'src/models/Language'
import { globalStyles } from 'src/global-styles'

import { useLanguage } from './LanguageContext'

const getAvailableFontsByLanguage = (language?: Language): FontFamily[] => {
    if (!!language && LANGUAGES_WITH_SPECIAL_FONTS.includes(language as LanguageWithSpecialFont)) {
        return [DEFAULT_FONT_FAMILY_BY_LANGUAGE[language]]
    }

    return ['Arial', 'Inter', 'Roboto', 'SourceSerif']
}

const getThemeConfigOverrides = (): Partial<ConfigurableThemeFields> => {
    const urlParams = new URLSearchParams(window.location.search)
    const overrides: Partial<ConfigurableThemeFields> = {}

    const themeTypeParam = urlParams.get(THEME_TYPE_QUERY_PARAM)
    if (THEME_TYPES.includes(themeTypeParam as ThemeType)) {
        overrides.themeType = themeTypeParam as ThemeType
    }

    const fontFamilyParam = urlParams.get(FONT_FAMILY_QUERY_PARAM)
    if (FONT_FAMILIES.includes(fontFamilyParam as FontFamily)) {
        overrides.fontFamily = fontFamilyParam as FontFamily
    }

    const textColorIndexParam = parseInt(urlParams.get(TEXT_COLOR_INDEX_QUERY_PARAM) || '')
    if (!isNaN(textColorIndexParam) && textColorIndexParam >= 0 && textColorIndexParam <= TEXT_COLORS_AMOUNT) {
        overrides.textColorIndex = textColorIndexParam
    }

    const textBackgroundColorIndexParam = parseInt(urlParams.get(TEXT_BACKGROUND_COLOR_INDEX_QUERY_PARAM) || '')
    if (
        !isNaN(textBackgroundColorIndexParam) &&
        textBackgroundColorIndexParam >= 0 &&
        textBackgroundColorIndexParam <= TEXT_BACKGROUND_COLORS_AMOUNT
    ) {
        overrides.textBackgroundColorIndex = textBackgroundColorIndexParam
    }

    const appBackgroundColorIndexParam = parseInt(urlParams.get(APP_BACKGROUND_COLOR_INDEX_QUERY_PARAM) || '')
    if (
        !isNaN(appBackgroundColorIndexParam) &&
        appBackgroundColorIndexParam >= 0 &&
        appBackgroundColorIndexParam <= APP_BACKGROUND_COLORS_AMOUNT
    ) {
        overrides.appBackgroundColorIndex = appBackgroundColorIndexParam
    }

    return overrides
}

type GetDefaultConfigurableFieldsParams = {
    language?: Language
    previousFields?: ConfigurableThemeFields
}
const getDefaultConfigurableFields = ({ language, previousFields }: GetDefaultConfigurableFieldsParams = {}): ConfigurableThemeFields => {
    let themeType = DEFAULT_THEME_TYPE
    let fontFamily = DEFAULT_FONT_FAMILY_BY_LANGUAGE[language || DEFAULT_LANGUAGE]
    let textColorIndex = DEFAULT_TEXT_COLOR_INDEX
    let textBackgroundColorIndex = DEFAULT_TEXT_BACKGROUND_COLOR_INDEX
    let appBackgroundColorIndex = DEFAULT_APP_BACKGROUND_COLOR_INDEX

    // this is not preserved in the storage, because it depends on the user, not on the preferences
    const headerHeight = previousFields?.headerHeight || HEADER_HEIGHT_DEFAULT

    const availableFonts = getAvailableFontsByLanguage(language)

    if (isChromakeyModeActive) {
        themeType = 'chromakey'
    } else {
        try {
            const {
                themeType: savedThemeType,
                fontFamily: savedFontFamily,
                textColorIndex: savedTextColorIndex,
                textBackgroundColorIndex: savedTextBackgroundColorIndex,
                appBackgroundColorIndex: savedAppBackgroundColorIndex,
            } = JSON.parse(localStorage.getItem(THEME_LOCAL_STORAGE_KEY) || '{}') as ConfigurableThemeFields

            if (THEME_TYPES.includes(savedThemeType)) {
                themeType = savedThemeType
            }

            if (availableFonts.includes(savedFontFamily)) {
                fontFamily = savedFontFamily
            }

            if (Number.isInteger(savedTextColorIndex) && savedTextColorIndex >= 0 && savedTextColorIndex <= TEXT_COLORS_AMOUNT - 1) {
                textColorIndex = savedTextColorIndex
            }

            if (
                Number.isInteger(savedTextBackgroundColorIndex) &&
                savedTextBackgroundColorIndex >= 0 &&
                savedTextBackgroundColorIndex <= TEXT_BACKGROUND_COLORS_AMOUNT - 1
            ) {
                textBackgroundColorIndex = savedTextBackgroundColorIndex
            }

            if (
                Number.isInteger(savedAppBackgroundColorIndex) &&
                savedAppBackgroundColorIndex >= 0 &&
                savedAppBackgroundColorIndex <= APP_BACKGROUND_COLORS_AMOUNT - 1
            ) {
                appBackgroundColorIndex = savedAppBackgroundColorIndex
            }
        } catch (e) {}
    }

    const config: ConfigurableThemeFields = {
        themeType,
        fontFamily,
        textColorIndex,
        textBackgroundColorIndex,
        appBackgroundColorIndex,
        headerHeight,
    }

    const configOverrides = getThemeConfigOverrides()
    if (configOverrides.fontFamily && !availableFonts.includes(configOverrides.fontFamily)) {
        delete configOverrides.fontFamily
    }
    Object.assign(config, configOverrides)

    return config
}

const verbitThemeExtension = { styles: { global: globalStyles } }

type ThemeContextData = {
    theme: Theme
    setThemeField: <T extends keyof ConfigurableThemeFields>(p1: T, p2: ConfigurableThemeFields[T]) => void
}

type ThemeProviderProps = { children?: React.ReactNode }

export const THEME_LOCAL_STORAGE_KEY = 'verbitext_ui_active_theme'

export const ThemeContext = createContext<ThemeContextData | null>(null)

export const ThemeProvider = (props: ThemeProviderProps) => {
    const { children } = props
    const { requestedLanguageName } = useLanguage()
    const [configurableFields, setConfigurableFields] = useState(getDefaultConfigurableFields())

    useEffect(() => {
        if (requestedLanguageName) {
            setConfigurableFields((previousFields) => getDefaultConfigurableFields({ language: requestedLanguageName, previousFields }))
        }
    }, [requestedLanguageName])

    useEffect(() => {
        if (isChromakeyModeActive) {
            return
        }
        try {
            localStorage.setItem(THEME_LOCAL_STORAGE_KEY, JSON.stringify(configurableFields))
        } catch (e) {}
    }, [configurableFields])

    const setThemeField: ThemeContextData['setThemeField'] = (field, value) =>
        setConfigurableFields((oldFields) => {
            const newFields: ConfigurableThemeFields = { ...oldFields, [field]: value }

            if (field === 'themeType') {
                // when user changes theme, reset text color and background color
                newFields.textColorIndex = DEFAULT_TEXT_COLOR_INDEX
                newFields.textBackgroundColorIndex = DEFAULT_TEXT_BACKGROUND_COLOR_INDEX

                analyticsService.track('AccessibilityThemeClicked', { themeType: value })
            }

            if (field === 'textColorIndex') {
                analyticsService.track('AccessibilityFontColorChange', { textColorIndex: value })
            }

            if (field === 'textBackgroundColorIndex') {
                analyticsService.track('AccessibilityBackgroundColorChange', { textBackgroundColorIndex: value })
            }

            if (field === 'fontFamily') {
                analyticsService.track('AccessibilityFontChange', { fontFamily: value })
            }

            return newFields
        })

    const providedValue = useMemo<ThemeContextData>(() => {
        const { themeType } = configurableFields

        const verbitTheme = themeType === 'dark' ? verbitThemeDark : verbitThemeDefault
        const verbitextTheme: Theme = {
            ...themeCore,
            ...configurableFields,
            palette: themePalettes[themeType],
            availableFontFamilies: getAvailableFontsByLanguage(requestedLanguageName),
        }

        // to minimize the impact of switching to Chakra onto the codebase,
        // and due to the difficulties of mapping our tokens onto Chakra's theme
        // (not only colors, but also shadows, gradients, images, etc.),
        // we simply merge the themes for the time being
        const theme = extendTheme(verbitTheme, verbitThemeExtension, verbitextTheme) as VerbitTheme & Theme

        return { theme, setThemeField }
    }, [configurableFields, requestedLanguageName])

    return (
        <ThemeContext.Provider value={providedValue}>
            <VerbitThemeProvider theme={providedValue.theme} cssVarsRoot="body">
                {children}
            </VerbitThemeProvider>
        </ThemeContext.Provider>
    )
}

export const useSetThemeField = () => {
    const ctx = useContext(ThemeContext)

    if (!ctx) {
        throw new Error('You have forgot to use <ThemeContext.Provider />, shame on you.')
    }

    return ctx.setThemeField
}
